import { useEffect, useRef, useState } from 'react';
import { gymApi } from '../api/gymApi';
import { useAuthed } from './useAuthed';
import type { SetLog } from '../../../shared/types';

/**
 * The most recent logged session for one exercise, used to pre-fill the current
 * one and to show whether each number beats last time.
 *
 * Keyed on the exercise name so switching variation re-fetches: the previous
 * numbers for a Neutral-Grip Pullup say nothing about a Lat Pulldown.
 * A failure here is non-fatal — autofill is a convenience, not a requirement —
 * so errors resolve to "no previous data" rather than blocking the workout.
 */
export function usePreviousPerformance(exerciseName: string | undefined) {
  const authed = useAuthed();
  const [sets, setSets] = useState<SetLog[]>([]);
  const [performedAt, setPerformedAt] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Held in a ref so the fetch below depends only on the exercise name. Listing
  // `authed` would re-fetch whenever that object's identity changed, and each
  // fetch sets state — an endless render loop.
  const authedRef = useRef(authed);
  authedRef.current = authed;

  useEffect(() => {
    if (!exerciseName) { setLoaded(true); return; }
    let cancelled = false;
    setLoaded(false);
    gymApi.getExerciseHistory(exerciseName, authedRef.current)
      .then(h => {
        if (cancelled) return;
        // History comes back newest first.
        const last = h.history[0];
        setSets(last?.sets ?? []);
        setPerformedAt(last?.performedAt ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setSets([]);
        setPerformedAt(null);
      })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [exerciseName]);

  return { previousSets: sets, previousPerformedAt: performedAt, previousLoaded: loaded };
}
